import { useEffect, useState } from 'react';
import { fetchSeasonWinners, driverDisplayName } from '../../services/jolpicaClient.js';
import Skeleton from '../common/Skeleton.jsx';

/**
 * SeasonOverview — lists all races in a season with their winners.
 * Shown when the user selects "All" in the track picker.
 * Tapping a race row navigates to its detailed results.
 *
 * Winners for the whole season are fetched in ONE call
 * (`/{season}/results/1/`) instead of one request per race.
 */
export default function SeasonOverview({ tracks, year, onSelectRace }) {
  const [winners, setWinners] = useState({});
  const [loading, setLoading] = useState(true);

  // Mounted with key={year} by ResultsScreen, so a year change remounts and
  // both pieces of state reset from their initialisers. Clearing them with a
  // synchronous setState in the effect body would cost an extra render pass.
  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      try {
        const racesWithWinners = await fetchSeasonWinners(year, { signal: ctrl.signal });
        const results = {};
        racesWithWinners.forEach((race) => {
          const winnerEntry = race.Results?.[0];
          if (!winnerEntry) return;
          const track = tracks.find((t) => String(t.round) === String(race.round));
          if (!track) return;
          results[track.id] = {
            driver: { abbr: winnerEntry.Driver?.code ?? '', name: driverDisplayName(winnerEntry.Driver) },
            time: winnerEntry.Time?.time ?? winnerEntry.status ?? '-',
          };
        });
        if (!ctrl.signal.aborted) {
          setWinners(results);
          setLoading(false);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[SeasonOverview]', err);
          setLoading(false);
        }
      }
    })();

    return () => ctrl.abort();
  }, [tracks, year]);

  // Round numbers skip cancelled races.
  let roundCounter = 0;

  return (
    <div className="mt-3 animate-fade-in">
      <div className="space-y-2">
        {tracks.map((track) => {
          const winner = winners[track.id];
          const isCancelled = track.is_cancelled;
          const isUpcoming = !track.isConcluded && !isCancelled;
          if (!isCancelled) roundCounter++;

          return (
            <button
              key={track.id}
              onClick={() => !isCancelled && onSelectRace(track.id)}
              disabled={isCancelled}
              className={`pressable relative flex w-full items-center gap-3 overflow-hidden rounded-xl px-3 py-3 text-left ring-1 transition-all ${
                isCancelled
                  ? 'bg-bg-card/50 ring-stroke/50 opacity-60'
                  : 'bg-bg-card ring-stroke hover:bg-bg-card2 hover:ring-stroke-strong'
              }`}
            >
              {/* Round number or cancelled icon */}
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bg-card2 text-ink-tertiary ring-1 ring-stroke">
                {isCancelled ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                  </svg>
                ) : (
                  <span className="font-mono text-xs font-bold tabular-nums">{roundCounter}</span>
                )}
              </div>

              {/* Race info */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-ink-primary">
                    {track.circuit_short_name}
                  </span>
                  {isCancelled ? (
                    <span className="shrink-0 rounded-full bg-ink-muted/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ink-muted line-through">
                      Cancelled
                    </span>
                  ) : isUpcoming ? (
                    <span className="shrink-0 rounded-full bg-signal-blue/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-signal-blue">
                      Upcoming
                    </span>
                  ) : null}
                </div>
                <div className="truncate text-2xs text-ink-tertiary">
                  {track.country_name}
                </div>
              </div>

              {/* Winner or loading state */}
              <div className="shrink-0 text-right">
                {isCancelled ? (
                  <span className="text-2xs text-ink-muted">—</span>
                ) : isUpcoming ? (
                  <span className="text-2xs text-ink-tertiary">TBD</span>
                ) : loading ? (
                  <Skeleton className="h-4 w-20" rounded="rounded" />
                ) : winner ? (
                  <div>
                    <div className="text-xs font-semibold text-ink-primary">
                      {winner.driver.abbr || winner.driver.name?.split(' ').pop()}
                    </div>
                    <div className="text-2xs text-ink-tertiary">
                      {winner.time}
                    </div>
                  </div>
                ) : (
                  <span className="text-2xs text-ink-muted">—</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <p className="mt-4 text-center text-[10px] uppercase tracking-widest-plus text-ink-muted">
        Tap a race to view full classification
      </p>
    </div>
  );
}
